/**
 * Slovenski zapis številk in datumov (CLAUDE.md, pravilo 10).
 *
 * Zneski se v aplikacijo prenašajo kot nizi z dvema decimalkama, da se
 * natančnost ne izgubi; oblikujemo jih šele ob prikazu.
 */

const ZNESEK = new Intl.NumberFormat('sl-SI', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  // Slovenski zapis v brskalniku stirimestnih stevil privzeto ne loci
  // (1248,77), pravilo 10 v CLAUDE.md pa zahteva 1.234,56. Zato izrecno.
  useGrouping: 'always',
});

const ODSTOTEK = new Intl.NumberFormat('sl-SI', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  useGrouping: 'always',
});

/** 1.234,56 — brez oznake valute, ker so stolpci ozki. */
export function znesek(vrednost: string): string {
  const stevilo = Number(vrednost);
  if (!Number.isFinite(stevilo)) return vrednost;
  // -0,00 je za bralca enako kot 0,00.
  return ZNESEK.format(stevilo === 0 ? 0 : stevilo);
}

/** 1.234,56 € — za posamezne, izpostavljene zneske. */
export function znesekZValuto(vrednost: string): string {
  return `${znesek(vrednost)} €`;
}

/** Ali je znesek negativen (za barvo). */
export function jeNegativen(vrednost: string): boolean {
  return Number(vrednost) < 0;
}

/** 31. 01. 2026 iz zapisa yyyy-mm-dd. */
export function datum(iso: string): string {
  const ujemanje = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!ujemanje) return iso;
  const [, leto, mesec, dan] = ujemanje;
  return `${dan!}. ${mesec!}. ${leto!}`;
}

/** 27. 09. 2026 ob 21:15 — trenutek (ISO) v krajevnem času brskalnika. */
export function datumInUra(iso: string): string {
  const kdaj = new Date(iso);
  if (Number.isNaN(kdaj.getTime())) return iso;
  const dvomestno = (n: number): string => String(n).padStart(2, '0');
  return `${dvomestno(kdaj.getDate())}. ${dvomestno(kdaj.getMonth() + 1)}. ${String(kdaj.getFullYear())} ob ${dvomestno(kdaj.getHours())}:${dvomestno(kdaj.getMinutes())}`;
}

/**
 * 12,3 % — sprememba glede na primerjalno obdobje.
 *
 * Prazna vrednost pomeni, da odstotka ni (primerjalno obdobje je nič in
 * deljenje z nič bi dalo neskončno), zato ostane celica prazna.
 */
export function odstotek(vrednost: string): string {
  if (vrednost === '') return '';
  const stevilo = Number(vrednost);
  if (!Number.isFinite(stevilo)) return vrednost;
  return `${ODSTOTEK.format(stevilo === 0 ? 0 : stevilo)} %`;
}

const EVRI = new Intl.NumberFormat('sl-SI', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  useGrouping: 'always',
});

/** 1.064.928 € — zaokrozeno na evre, za ploscice nadzorne plosce. */
export function evri(vrednost: string): string {
  const stevilo = Math.round(Number(vrednost));
  if (!Number.isFinite(stevilo)) return vrednost;
  return `${EVRI.format(stevilo === 0 ? 0 : stevilo)} €`;
}

/** Stevilo s slovenskim zapisom in danim stevilom decimalk (1,95). */
export function decimalno(vrednost: string, decimalk: number): string {
  const stevilo = Number(vrednost);
  if (!Number.isFinite(stevilo)) return vrednost;
  return new Intl.NumberFormat('sl-SI', {
    minimumFractionDigits: decimalk,
    maximumFractionDigits: decimalk,
    useGrouping: 'always',
  }).format(stevilo === 0 ? 0 : stevilo);
}
